/**
 * Jobs Service
 *
 * Frontend service for interacting with the workflow job queue API.
 *
 * NOTE: This service reads the auth token directly from localStorage for consistency
 * with other services (workflowService, etc.) and to avoid token staleness issues
 * that can occur when the React state token gets out of sync with localStorage
 * during token refresh cycles.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

export interface WorkflowJob {
  id: string;
  workflowType: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'dead_letter' | 'cancelled';
  progress: number;
  progressMessage: string | null;
  attempts: number;
  maxAttempts: number;
  lastError: string | null;
  savedReportId: string | null;
  notificationSent: boolean;
  customerId: string | null;
  projectId: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  customer?: {
    id: string;
    name: string;
  };
}

export interface SubmitJobRequest {
  workflowType: string;
  customerId?: string;
  projectId?: string;
  documents?: string[];
  additionalDocuments?: string[];
  additionalContext?: string;
  context?: string;
  pastReports?: string[];
  additionalInfo?: string;
  analysisMode?: string;
  scenarioText?: string;
  reportTitle?: string;
  reportOptions?: Record<string, boolean>;
}

export interface SubmitJobResponse {
  success: boolean;
  jobId: string;
  status: string;
  message: string;
  workflowType: string;
}

export interface GetJobResponse {
  success: boolean;
  job: WorkflowJob;
}

export interface GetUserJobsResponse {
  success: boolean;
  jobs: WorkflowJob[];
}

class JobsService {
  private static instance: JobsService;

  private constructor() {}

  public static getInstance(): JobsService {
    if (!JobsService.instance) {
      JobsService.instance = new JobsService();
    }
    return JobsService.instance;
  }

  /**
   * Get authentication headers using token from localStorage.
   * This ensures we always use the most current token, avoiding issues
   * where React state might be stale after a token refresh.
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('riskninja-token');
    return {
      'Authorization': token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    };
  }

  /**
   * Submit a new workflow job
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async submitJob(_token: string, request: SubmitJobRequest): Promise<SubmitJobResponse> {
    const response = await fetch(`${API_BASE_URL}/api/jobs/workflows`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to submit job');
    }

    return response.json();
  }

  /**
   * Get a specific job by ID
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async getJob(_token: string, jobId: string): Promise<GetJobResponse> {
    const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get job');
    }

    return response.json();
  }

  /**
   * Get user's active and recent jobs
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async getUserJobs(_token: string, includeCompleted = false): Promise<GetUserJobsResponse> {
    const url = new URL(`${API_BASE_URL}/api/jobs/user/active`);
    if (includeCompleted) {
      url.searchParams.append('includeCompleted', 'true');
    }

    const response = await fetch(url.toString(), {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get user jobs');
    }

    return response.json();
  }

  /**
   * Get jobs with pending notifications
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async getPendingNotifications(_token: string): Promise<GetUserJobsResponse> {
    const response = await fetch(`${API_BASE_URL}/api/jobs/user/pending-notifications`, {
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get pending notifications');
    }

    return response.json();
  }

  /**
   * Mark a job's notification as sent
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async markNotificationSent(_token: string, jobId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}/mark-notified`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to mark notification');
    }
  }

  /**
   * Cancel a pending job
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async cancelJob(_token: string, jobId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to cancel job');
    }
  }

  /**
   * Retry a failed job
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async retryJob(_token: string, jobId: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}/retry`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to retry job');
    }
  }

  /**
   * Check if the queue is available by trying to get jobs
   * Returns true if the API responds successfully
   * @param _token - Deprecated: token is now read from localStorage. Parameter kept for backwards compatibility.
   */
  async isQueueAvailable(_token: string): Promise<boolean> {
    try {
      await this.getUserJobs(_token);
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const jobsService = JobsService.getInstance();
